import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  Globe,
  Database,
  BookOpen,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  ChevronRight,
  X,
  Layers
} from 'lucide-react';
import api from '../lib/api';

const SOURCE_COLORS = [
  "#ef4444", "#3b82f6", "#f59e0b", "#8b5cf6", "#10b981",
  "#ec4899", "#06b6d4", "#f97316", "#6366f1", "#14b8a6"
];

const HighlightedReport = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [hoveredSentence, setHoveredSentence] = useState(null);
  const [hiddenSources, setHiddenSources] = useState(new Set());
  const sentenceRefs = useRef({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/submissions/${id}/highlighted-text`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load highlighted report');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const toggleSource = (idx) => {
    setHiddenSources(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const getSourceIcon = (type) => {
    switch (type) {
      case 'web': return Globe;
      case 'internal': return Database;
      case 'publication': return BookOpen;
      default: return Globe;
    }
  };

  const getTopMatch = (matches) => {
    if (!matches || matches.length === 0) return null;
    const visible = matches.filter(m => !hiddenSources.has(m.source_index));
    return visible.length > 0 ? visible[0] : null;
  };

  const scrollToFirstMatch = (sourceIndex) => {
    setSelectedSource(sourceIndex);
    // Find first sentence matching this source
    if (!data) return;
    for (const [paraIdx, para] of data.paragraphs.entries()) {
      for (const [sentIdx, sent] of para.sentences.entries()) {
        const match = sent.matches?.find(m => m.source_index === sourceIndex);
        if (match) {
          const key = `${paraIdx}-${sentIdx}`;
          const el = sentenceRefs.current[key];
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHoveredSentence(key);
            setTimeout(() => setHoveredSentence(null), 2000);
          }
          return;
        }
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
          <h2 className="font-serif text-2xl font-bold mb-2">Unable to Load Report</h2>
          <p className="text-muted-foreground mb-4">{error || 'Report data unavailable.'}</p>
          <Link to={`/submission/${id}`}>
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Submission
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const matchedPct = data.total_sentences > 0
    ? Math.round((data.matched_sentences / data.total_sentences) * 100)
    : 0;

  return (
    <Layout>
      <div className="space-y-4" data-testid="highlighted-report-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <Link
              to={`/submission/${id}`}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-1"
              data-testid="back-to-submission"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Submission
            </Link>
            <h1 className="font-serif text-2xl font-bold text-foreground flex items-center gap-2">
              <Layers className="w-6 h-6 text-primary" />
              Highlighted Similarity Report
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">{data.title}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Similarity</p>
              <p className={`text-xl font-bold font-mono ${
                data.risk_level === 'high' ? 'text-red-600' :
                data.risk_level === 'moderate' ? 'text-amber-600' : 'text-emerald-600'
              }`}>
                {data.similarity_score?.toFixed(1)}%
              </p>
            </div>
            <Separator orientation="vertical" className="h-10" />
            <div className="text-right">
              <p className="text-xs text-muted-foreground">AI Content</p>
              <p className="text-xl font-bold font-mono text-purple-600">
                {data.ai_score?.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-muted-foreground">
                <strong className="text-foreground">{data.total_sentences}</strong> sentences analyzed
              </span>
              <span className="text-muted-foreground">
                <strong className="text-foreground">{data.matched_sentences}</strong> with matches ({matchedPct}%)
              </span>
              <span className="text-muted-foreground">
                <strong className="text-foreground">{data.sources.length}</strong> sources detected
              </span>
              <div className="flex-1" />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-red-100 border border-red-300" />
                <span className="text-xs text-muted-foreground">Matched text</span>
                <div className="w-3 h-3 rounded-sm bg-white border border-border ml-2" />
                <span className="text-xs text-muted-foreground">Original text</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main content: Document + Source panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Document text - Left panel (2/3 width) */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader className="py-3 border-b border-border/40">
                <CardTitle className="font-serif text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Document Text
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 max-h-[70vh] overflow-y-auto" data-testid="document-text-panel">
                <div className="space-y-4 leading-relaxed text-sm">
                  {data.paragraphs.map((para, paraIdx) => (
                    <p key={paraIdx} className="text-foreground">
                      {para.sentences.map((sent, sentIdx) => {
                        const topMatch = getTopMatch(sent.matches);
                        const key = `${paraIdx}-${sentIdx}`;
                        const isHovered = hoveredSentence === key;
                        const isSelectedSource = topMatch && selectedSource === topMatch.source_index;
                        const color = topMatch ? (data.sources[topMatch.source_index]?.color || SOURCE_COLORS[0]) : null;

                        if (!topMatch) {
                          return (
                            <span key={sentIdx} ref={el => sentenceRefs.current[key] = el}>
                              {sent.text}{' '}
                            </span>
                          );
                        }

                        const opacity = Math.min(0.35, topMatch.similarity / 200);

                        return (
                          <span
                            key={sentIdx}
                            ref={el => sentenceRefs.current[key] = el}
                            data-testid={`matched-sentence-${paraIdx}-${sentIdx}`}
                            className={`relative cursor-pointer rounded-sm px-0.5 transition-all duration-200 ${
                              isHovered ? 'ring-2 ring-offset-1' : ''
                            } ${isSelectedSource ? 'ring-1 ring-offset-1' : ''}`}
                            style={{
                              backgroundColor: `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`,
                              borderBottom: `2px solid ${color}`,
                              ringColor: color
                            }}
                            onMouseEnter={() => setHoveredSentence(key)}
                            onMouseLeave={() => setHoveredSentence(null)}
                            onClick={() => setSelectedSource(topMatch.source_index)}
                            title={`Source ${topMatch.source_index + 1}: ${data.sources[topMatch.source_index]?.source_title} (${topMatch.similarity}% match)`}
                          >
                            <span className="relative z-10">{sent.text}</span>
                            {isHovered && (
                              <span
                                className="absolute -top-7 left-0 z-20 px-2 py-0.5 text-xs font-medium text-white rounded shadow-lg whitespace-nowrap"
                                style={{ backgroundColor: color }}
                              >
                                Source {topMatch.source_index + 1} ({topMatch.similarity}%)
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </p>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Source panel - Right (1/3 width) */}
          <div className="lg:col-span-1">
            <Card className="h-full">
              <CardHeader className="py-3 border-b border-border/40">
                <CardTitle className="font-serif text-base">
                  Matched Sources ({data.sources.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-[70vh] overflow-y-auto" data-testid="sources-panel">
                {data.sources.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground text-sm">
                    No matched sources found.
                  </div>
                ) : (
                  <div className="divide-y divide-border/40">
                    {data.sources.map((source, idx) => {
                      const Icon = getSourceIcon(source.source_type);
                      const isSelected = selectedSource === idx;
                      const isHidden = hiddenSources.has(idx);
                      return (
                        <div
                          key={idx}
                          data-testid={`source-item-${idx}`}
                          className={`p-3 transition-colors cursor-pointer ${
                            isSelected ? 'bg-muted/60' : 'hover:bg-muted/30'
                          }`}
                          onClick={() => scrollToFirstMatch(idx)}
                        >
                          <div className="flex items-start gap-2">
                            <div
                              className="w-6 h-6 rounded-sm flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5"
                              style={{ backgroundColor: source.color }}
                            >
                              {idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground leading-tight line-clamp-2">
                                {source.source_title}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs px-1.5 py-0">
                                  {source.percentage_matched?.toFixed(1)}%
                                </Badge>
                                <span className="text-xs text-muted-foreground capitalize flex items-center gap-1">
                                  <Icon className="w-3 h-3" />
                                  {source.source_type}
                                </span>
                              </div>
                              {source.source_url && (
                                <a
                                  href={source.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-0.5"
                                  onClick={e => e.stopPropagation()}
                                >
                                  View source <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                              )}
                              {isSelected && source.matched_text && (
                                <div className="mt-2 p-2 bg-muted/50 rounded-sm border-l-2" style={{ borderColor: source.color }}>
                                  <p className="text-xs text-muted-foreground italic line-clamp-4">
                                    "{source.matched_text}"
                                  </p>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleSource(idx); }}
                              className="text-muted-foreground hover:text-foreground p-0.5 flex-shrink-0"
                              title={isHidden ? 'Show highlights' : 'Hide highlights'}
                              data-testid={`toggle-source-${idx}`}
                            >
                              {isHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-muted/50 border border-border rounded-sm p-3">
          <p className="text-xs text-muted-foreground">
            Highlighted text indicates potential matches with external sources. Color intensity reflects match confidence.
            Click on highlighted text to view the matched source. This is a tool to assist review &mdash; matched text
            may include properly cited quotations, common phrases, or coincidental similarities.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default HighlightedReport;
